import { Component, EventEmitter, Input, Output } from '@angular/core';
import { BaseTechnicalSectionComponent } from '../../common/base-technical-section.component';
import { UICollectionTypes } from '../../../entities/enums/ui-collection-types.enum';
import { EnvironmentService } from 'src/app/services/environment.service';
import { ENERGY_TYPES } from '../../../constants/grid-application-constants';
import { detectDecimalSeparator } from '../../../utils/utils';
import { PhotovoltaicModule } from '../../../entities/photovoltaicModule';
import { PowerInverter } from '../../../entities/powerInverter';
import { IntegratedGridProtectionDocument } from '../../../entities/integratedGridProtectionDocument';
import { GridApplicationCommentsService } from 'src/app/+grid-application-shared/services/grid-application-comments.service';
import { ControlContainer, NgForm } from '@angular/forms';
import { GridApplicationDocumentTypes } from 'src/app/+grid-application-shared/entities/enums/grid-application-document-types.enum';
import { Document } from '../../../../jurisdiction/jurisdiction.domain';

@Component({
  selector: 'app-photovoltaics-data',
  templateUrl: './photovoltaics-data.component.html',
  styleUrls: ['./photovoltaics-data.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class PhotovoltaicsDataComponent extends BaseTechnicalSectionComponent {
  @Input() isIntegratedGridProtection: boolean;
  @Input() showFotoProofPhotovoltaicModules: boolean = false;
  @Output() warningsVisible = new EventEmitter<boolean>();

  public readonly UI_COLLECTION_TYPES = UICollectionTypes;
  public readonly detectDecimalSeparator = detectDecimalSeparator;

  pvModulesPowerNotEqual: boolean;
  invertersPowerNotEqual: boolean;

  constructor(commentService: GridApplicationCommentsService, environmentService: EnvironmentService) {
    super(commentService, environmentService);
  }

  public showPhotovoltaicsDataSection(): boolean {
    return this.technicalData.existing_energy_producers[0].energy_type === ENERGY_TYPES.PHOTOVOLTAICS;
  }

  public addPvModule(): void {
    this.technicalData.photovoltaic_modules.push(new PhotovoltaicModule());
    this.calculatePhotovoltaicsPowerDifference();
  }

  public removePvModule(pvModule: PhotovoltaicModule): void {
    this.technicalData.photovoltaic_modules = this.technicalData.photovoltaic_modules.filter(x => x !== pvModule);
    this.calculatePhotovoltaicsPowerDifference();
  }

  public addPowerInverter() {
    this.technicalData.power_inverters.push(new PowerInverter());
    this.calculateInvertersPowerDifference();

    this.addGridProtectionDoc(UICollectionTypes.PowerInverter);
  }

  public removePowerInverter(event: any): void {
    const index = this.technicalData.power_inverters.indexOf(event.element);

    this.technicalData.power_inverters = this.technicalData.power_inverters.filter(x => x !== event.element);
    this.calculateInvertersPowerDifference();

    this.removeGridProtectionDoc(index, event.type);
  }

  public calculatePhotovoltaicsPowerDifference(inputChanged: boolean = false): void {
    const total = this.technicalData.photovoltaic_modules.reduce(
      (value, module) => value + (module.identical_modules * module.power_single_pv_module) / 1000,
      0
    );

    this.pvModulesPowerNotEqual = Math.round(total * 1000) / 1000 !== this.technicalData.total_power_pv_modules;
    this.emitWarningsVisible();
    if (inputChanged) {
      this.technicalData.feed_in_management = null;
    }
  }

  public calculateInvertersPowerDifference() {
    const total = this.technicalData.power_inverters.reduce(
      (value, inverter) => value + inverter.identical_units * inverter.max_unit_power,
      0
    );

    this.invertersPowerNotEqual = Math.round(total * 1000) / 1000 !== this.technicalData.total_power_inverters;
    this.emitWarningsVisible();
  }

  private emitWarningsVisible(): void {
    this.warningsVisible.emit(this.invertersPowerNotEqual || this.pvModulesPowerNotEqual);
  }

  public addDocument(moduleIndex: number): void {
    this.technicalData.photovoltaic_modules[moduleIndex].foto_proof_photovoltaic_modules.push(
      new Document(GridApplicationDocumentTypes.FotoProofPhotovoltaicModules)
    );
    this.technicalForm.form.markAsDirty();
  }

  public trackBy(ph: number): number {
    return ph;
  }

  public removeDocument(moduleIndex: number, documentIndex: number) {
    this.technicalForm.form.markAsDirty();
    this.technicalData.photovoltaic_modules[moduleIndex].foto_proof_photovoltaic_modules.splice(documentIndex, 1);
  }

  protected get sectionComment(): string | null {
    return this.technicalData.technical_specifications_sections_state?.detailed_info_photovoltaics_comment;
  }

  protected set sectionComment(value: string | null) {
    this.technicalData.technical_specifications_sections_state.detailed_info_photovoltaics_comment = value;
  }

  protected get sectionLocked(): boolean | null {
    return this.technicalData.technical_specifications_sections_state?.is_detailed_info_photovoltaics_locked;
  }

  protected set sectionLocked(value: boolean | null) {
    this.technicalData.technical_specifications_sections_state.is_detailed_info_photovoltaics_locked = value;
  }

  // TODO : This would probably be moved in base, but stays here for now
  private addGridProtectionDoc(documentType: string): void {
    if (this.isIntegratedGridProtection) {
      this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.push(
        new IntegratedGridProtectionDocument(documentType)
      );
    }
  }

  private removeGridProtectionDoc(index: number, unitType: string): void {
    if (this.isIntegratedGridProtection) {
      const documentsToFilter = this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.filter(
        d => d.unit_type === unitType
      );

      this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents =
        this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.filter(d => d.unit_type !== unitType);

      documentsToFilter.splice(index, 1);

      this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.push(...documentsToFilter);
    }
  }
}
